import { Box, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";

import { PropertyEditCurrentValue } from "@/components/property_edit/PropertyEditHistoryRow";
import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { LoanInfoHistoryEditor } from "./HistoryEditor";
import { LoanInfoHistoryItem } from "./HistoryItem";
import { InterestRateEditor } from "./InterestRateEditor";
import { LoanAmountPeriodEditor } from "./LoanAmountPeriodEditor";
import { LoanType } from "./types";

interface props {
  loanType: LoanType;
  onClickPageBack: () => void;
}

export const EditPropertySectionLoanInfoHistory: React.FC<props> = ({ loanType, onClickPageBack }) => {
  // 選択済み物件取得
  const selected_property = useRecoilValue(SelectedProperty);
  const [recordVersion, setRecordVersion] = React.useState(0);
  const handleOnCreate = () => setRecordVersion((version) => version + 1);

  const getLoanDataByType = (loanType: LoanType, data: any) => {
    switch (loanType) {
      case "借入利率変動":
        return data.loan_interest_rate_percentage;
      case "繰上返済":
        return data.loan_prepayment;
      default:
        return undefined;
    }
  };

  const filteredAndSortedResults =
    selected_property?.user_input_monthly_cashflow_results
      ?.filter((result) => {
        const loanData = getLoanDataByType(loanType, result);
        return loanData !== undefined && loanData !== null;
      })
      .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime()) || [];

  // 最新のデータ（最初の1件）と過去のデータ（2件目以降）を分離
  const latestResult = filteredAndSortedResults[0];

  if (loanType === "借入条件")
    return (
      <BaseTemplate headerTitle={loanType} onBack={onClickPageBack} backLabel="借入">
        <LoanAmountPeriodEditor initiallyEditing onCancel={onClickPageBack} />
      </BaseTemplate>
    );
  return (
    <BaseTemplate headerTitle={loanType} onBack={onClickPageBack} backLabel="借入" isBackGroundWhite>
      <PropertyEditCurrentValue
        label={loanType === "借入利率変動" ? "現在の金利" : undefined}
        value={
          latestResult
            ? `${Number(getLoanDataByType(loanType, latestResult)).toLocaleString()}${
                loanType === "借入利率変動" ? "%" : "円"
              }`
            : "未登録"
        }
        since={latestResult ? dayjs(latestResult.month).format("YYYY年M月") : undefined}
      />
      <Typography component="h3" data-edit-section>
        変更を記録する
      </Typography>
      {loanType === "借入利率変動" ? (
        <InterestRateEditor key={recordVersion} onCancel={onClickPageBack} onSaved={handleOnCreate} />
      ) : (
        <LoanInfoHistoryEditor
          key={recordVersion}
          onCancel={onClickPageBack}
          onCreate={handleOnCreate}
          propertyBoughtAt={selected_property?.property.bought_at}
        />
      )}
      <Box mt={4}>
        <Typography component="h3" data-edit-section>
          これまでの金利・返済
        </Typography>
        {filteredAndSortedResults.map((result) => (
          <LoanInfoHistoryItem key={result.month} monthlyCashflowResult={result} loanType={loanType} />
        ))}
        <Typography variant="body2" color="textSecondary">
          行をタップすると、記録を訂正したり削除したりできます。
        </Typography>
      </Box>
    </BaseTemplate>
  );
};
